import React, { useEffect, useMemo, useRef, useState } from "react";
import RichTextEditor, { EditorValue } from "react-rte";
import { Button, FormControl, FormLabel, useTheme } from "@mui/material";
import classNames from "classnames";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import chroma from "chroma-js";
type IRichTextEditorProps = React.ComponentProps<React.JSXElementConstructor<any>>;

export interface IRichTextFieldProps extends Omit<IRichTextEditorProps, "value"> {
  label?: string;
  name?: string;
  value?: string | EditorValue;
  disabled?: boolean;
  onFocus?: (event?: any) => any;
  onBlur?: (event?: any) => any;
  onChange?: (event: any) => any;
  meta?: Partial<FieldMetaProps<any>>;
  className?: string;
  id?: string;
  placeholder?: string;
  dataType?: "html" | EditorValue;
  templateHtml?: string;
}

// Optionally specify the groups to display (displayed in the order listed).
const toolbarConfig: IRichTextEditorProps["toolbarConfig"] = {
  display: [
    "INLINE_STYLE_BUTTONS",
    "BLOCK_TYPE_BUTTONS",
    "LINK_BUTTONS",
    "IMAGE_BUTTON",
    "BLOCK_TYPE_DROPDOWN",
    "HISTORY_BUTTONS",
    "BLOCK_ALIGNMENT_BUTTONS",
  ],
  INLINE_STYLE_BUTTONS: [
    { label: "Bold", style: "BOLD", className: "rte-button" },
    { label: "Italic", style: "ITALIC", className: "rte-button" },
    { label: "Underline", style: "UNDERLINE", className: "rte-button" },
  ],
  BLOCK_TYPE_DROPDOWN: [
    { label: "Normal", style: "unstyled", className: "rte-button" },
    { label: "Heading Large", style: "header-one", className: "rte-button" },
    { label: "Heading Medium", style: "header-two", className: "rte-button" },
    { label: "Heading Small", style: "header-three", className: "rte-button" },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: "UL", style: "unordered-list-item", className: "rte-button" },
    { label: "OL", style: "ordered-list-item", className: "rte-button" },
  ],
  BLOCK_ALIGNMENT_BUTTONS: [],
};

/**  The editor is changing undefined and null values into empty html blocks, strip does out */
const noChangeIfUnchanged = (meta: any, html: string): string => {
  if (!!meta && !meta?.initialValue) {
    return html === "<p></p>" || html === "<p><br></p>" ? meta?.initialValue : html;
  } else {
    return html;
  }
};

// RICHTEXTFIELD COMPONENT
export const RichTextField = (props: IRichTextFieldProps): JSX.Element => {
  const {
    label,
    name,
    onFocus,
    onBlur,
    onChange,
    value,
    className,
    disabled,
    meta,
    id,
    placeholder,
    dataType,
    templateHtml,
    customControls,
    ...editorProps
  } = props;
  const type = dataType ?? "html";
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [editorValue, setEditorValue] = useState<EditorValue>(RichTextEditor.createEmptyValue());
  const theme = useTheme();
  const editorRef = useRef<any>();

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  /** Set the input id attribute for the content area of the editor as there is no way to set it through props */
  useEffect(() => {
    const content = editorRef?.current?.editor?.editor;
    if (content?.setAttribute) content.setAttribute("id", name);
  }, [name, editorRef]);

  useEffect(() => {
    const newValue =
      !!value && typeof value === "string"
        ? RichTextEditor.createValueFromString(value, "html")
        : RichTextEditor.createEmptyValue();
    setEditorValue(newValue);
  }, [value, type]);

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>): void => {
    setIsFocused(true);
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    setIsFocused(false);
    // Output changes on blur.
    const outputValue = editorValue?.toString
      ? noChangeIfUnchanged(value as string, editorValue.toString("html"))
      : null;
    if (onChange && typeof onChange === "function") {
      onChange(outputValue);
    }
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  /** Template button which is enabled only if the rich text field is empty */
  const addLoadTemplateButton = useMemo(() => {
    return templateHtml
      ? () => {
          const currentValue = editorValue?.toString ? noChangeIfUnchanged(null, editorValue.toString("html")) : null;
          const isEmpty = !currentValue || currentValue === "<p></p>" || currentValue === "<p><br></p>";
          return (
            <Button
              key="template_button"
              color="primary"
              size="small"
              variant="contained"
              style={{ fontSize: 12, padding: "4px 8px", margin: "0px 5px 5px 0px" }}
              disabled={!isEmpty} // If there's content loaded, disable the button.
              onClick={() => {
                const template = RichTextEditor.createValueFromString(templateHtml, "html");
                setEditorValue(template);
              }}
            >
              Load Template
            </Button>
          );
        }
      : null;
  }, [templateHtml, editorValue]);

  /** Clear button which facilitates clearing the editor of content */
  const addClearButton = useMemo(() => {
    return () => {
      return (
        <Button
          key="clear_button"
          color="primary"
          size="small"
          variant="contained"
          style={{ fontSize: 12, padding: "4px 8px", margin: "0px 5px 5px 0px" }}
          onClick={() => {
            const clear = RichTextEditor.createEmptyValue();
            setEditorValue(clear);
          }}
        >
          Clear
        </Button>
      );
    };
  }, []);

  const handleOnChanges = (updatedValue: EditorValue): void => {
    setEditorValue(updatedValue);
  };

  const hasError = meta?.error ? true : false;
  const editorStyles = {
    minHeight: "200px",
    backgroundColor: theme.custom.forms.inputBack,
    color: theme.palette.common.white,
    fontFamily: "'Roboto', sans-serif",
    width: "100%",
    border: "none!important",
    maxHeight: "300px",
    overflowY: "scroll",
  };
  const toolbarStyles = {
    backgroundColor: chroma(theme.custom.forms.inputBack).darken(0.5).css(),
    border: `1px solid ${chroma(theme.custom.forms.inputBack).brighten(1).css()}`,
    color: "#fff",
    padding: 5,
    paddingBottom: 3,
    margin: 0,
  };

  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error"
      )}
      error={hasError}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: 0,
        flexWrap: "wrap",
        width: "100%",
        "& .input-base": {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flexGrow: 1,
          border: `1px solid ${theme.palette.primary.dark} !important`,
        },
        "option, button": {
          background: `${theme.palette.primary.main} !important`,
          color: `${theme.palette.common.white} !important`,
          border: `1px solid ${theme.palette.primary.dark} !important`,
          "> span": {
            filter: "invert(100%)",
          },
        },
      }}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <RichTextEditor
        ref={editorRef}
        className="input-base"
        editorClassName="react_rte_editor"
        value={editorValue}
        onChange={handleOnChanges}
        toolbarConfig={toolbarConfig}
        editorStyle={editorStyles}
        toolbarStyle={toolbarStyles}
        customControls={
          customControls && templateHtml
            ? [...customControls, ...[addLoadTemplateButton, addClearButton]]
            : customControls && !templateHtml
              ? customControls
              : !customControls && templateHtml
                ? [addLoadTemplateButton, addClearButton]
                : [addClearButton]
        }
        {...editorProps}
      />
      <FieldError meta={meta} />
    </FormControl>
  );
};
